import React, { useEffect, useState } from 'react';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { format, startOfDay } from 'date-fns';
import type { TimeSlot, Booking } from '../types/availability';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
}

type TimestampOrDate = FirestoreTimestamp | Date;

interface RawBooking extends Omit<Booking, 'startTime' | 'endTime' | 'createdAt'> {
  startTime: FirestoreTimestamp;
  endTime: FirestoreTimestamp;
  createdAt: FirestoreTimestamp;
}

interface SlotActionModalProps {
  slot: TimeSlot;
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  onModify: () => void;
}

const SlotActionModal: React.FC<SlotActionModalProps> = ({
  slot,
  isOpen,
  onClose,
  onDelete,
  onModify,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Click outside to close modal (must be before early return)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click is on the backdrop (not the modal content)
      if (target.classList.contains('action-modal-backdrop')) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  console.log('Raw slot:', slot);
  console.log('Raw bookings:', slot.bookings);

  // Convert booking dates to proper Date objects
  const bookings = (slot.bookings || []).map(booking => {
    const convertToDate = (time: TimestampOrDate) => {
      if (time instanceof Date) return time;
      if ('seconds' in time) return new Date(time.seconds * 1000);
      return new Date(time);
    };

    return {
      ...booking,
      startTime: convertToDate(booking.startTime),
      endTime: convertToDate(booking.endTime)
    };
  });

  console.log('Processed bookings:', bookings);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      if (bookings.length > 0) {
        // Use backend to handle refunds and notifications
        await axios.post(`${API_URL}/api/bookings/delete-slot`, {
          slotId: slot.id,
          starName: slot.starName || 'Star',
        });
        toast.success(`Slot deleted. ${bookings.length} booking(s) refunded.`);
      } else {
        // No bookings — safe to delete directly
        await deleteDoc(doc(db, 'slots', slot.id));
        toast.success('Slot deleted.');
      }
      onDelete();
      onClose();
    } catch (error) {
      console.error('Error deleting slot:', error);
      toast.error('Failed to delete slot. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="action-modal-backdrop fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">Manage Slot</h2>
        
        <div className="space-y-2 mb-4">
          <p className="text-gray-700">
            <span className="font-medium">Date:</span> {format(slot.startTime, 'MMMM d, yyyy')}
          </p>
          <p className="text-gray-700">
            <span className="font-medium">Time:</span> {format(slot.startTime, 'h:mm a')} - {format(slot.endTime, 'h:mm a')}
            {startOfDay(slot.startTime).getTime() !== startOfDay(slot.endTime).getTime() && (
              <span className="text-sm text-gray-500"> ({format(slot.endTime, 'MMM d')})</span>
            )}
          </p>
          <p className="text-gray-700">
            <span className="font-medium">Duration:</span> {slot.duration} minutes
          </p>
          {bookings.length > 0 && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
              <p className="font-medium text-amber-800">This slot has bookings:</p>
              <ul className="ml-4 text-sm mt-2 space-y-1 text-amber-700">
                {bookings.map((booking, index) => (
                  <li key={index}>
                    {format(booking.startTime, 'h:mm a')} - {format(booking.endTime, 'h:mm a')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {showDeleteConfirm ? (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800 font-medium mb-2">
              Are you sure you want to delete this slot?
              {bookings.length > 0 && (
                <span className="block text-sm mt-1">
                  This slot has {bookings.length} active booking(s). Fans will be refunded and notified.
                </span>
              )}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 rounded-md font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              Close
            </button>
            <button
              onClick={onModify}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Modify
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlotActionModal;